import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Store, Package, Sparkles, Sun, Moon } from 'lucide-react';

export default function Storefront() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('storefront_theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.remove('light-storefront');
      document.documentElement.classList.add('dark-storefront');
    } else {
      document.documentElement.classList.remove('dark-storefront');
      document.documentElement.classList.add('light-storefront');
    }
    localStorage.setItem('storefront_theme', isDark ? 'dark' : 'light');
    return () => {
      document.documentElement.classList.remove('light-storefront', 'dark-storefront');
    };
  }, [isDark]);

  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        const [settingsRes, productsRes, categoriesRes] = await Promise.all([
          api.get('/public/settings'),
          api.get('/public/products'),
          api.get('/public/categories')
        ]);
        
        const config = settingsRes.data;
        setSettings(config);
        setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
        setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
        
        // Apply branding
        if (config.brand_color) document.documentElement.style.setProperty('--brand', config.brand_color);
        else document.documentElement.style.removeProperty('--brand');
        
        if (config.theme_bg_main) document.documentElement.style.setProperty('--bg-main', config.theme_bg_main);
        else document.documentElement.style.removeProperty('--bg-main');
        
        if (config.theme_text_main) document.documentElement.style.setProperty('--text-main', config.theme_text_main);
        else document.documentElement.style.removeProperty('--text-main');
        
      } catch (err) {
        console.error('Failed to load storefront data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPublicData();
  }, []);

  const safeProducts = Array.isArray(products) ? products : [];
  const filteredProducts = safeProducts.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || p.categoryId === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-theme-main flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand"></div>
      </div>
    );
  }

  const storeName = settings.store_name || 'Nexus Market';
  const showLogo = settings.logo_display_mode !== 'title';
  const showTitle = settings.logo_display_mode !== 'logo';

  return (
    <div className="min-h-screen bg-theme-main text-theme-main font-sans selection:bg-brand selection:text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-theme-card/80 backdrop-blur-md border-b border-theme shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {showLogo && (
              settings.store_logo ? (
                <img src={settings.store_logo} alt={storeName} className="h-10 w-10 object-contain rounded-xl bg-white shadow-sm" />
              ) : (
                <div className="p-2.5 rounded-xl text-white shadow-lg bg-brand">
                  <Store size={24} />
                </div>
              )
            )}
            {showTitle && (
              <h1 className="text-2xl font-black tracking-tight">{storeName}</h1>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2.5 rounded-xl bg-theme-main border border-theme text-theme-muted hover:text-brand hover:border-brand transition-all duration-300 hover:scale-105"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link to="/login" className="text-sm font-bold text-theme-muted hover:text-brand transition-colors">Staff Login</Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-brand/5 -skew-y-3 transform origin-top-left -z-10"></div>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand/10 text-brand text-xs font-black uppercase tracking-widest mb-6">
            <Sparkles size={14} /> Welcome to the store
          </span>
          <h2 className="text-5xl md:text-7xl font-black tracking-tighter mb-6 leading-tight">
            Discover <span className="text-brand">Premium</span> Quality.
          </h2>
          <p className="text-lg md:text-xl text-theme-muted max-w-2xl mx-auto font-medium leading-relaxed">
            Browse our curated selection of products. From fresh groceries to premium electronics, we have everything you need in one place.
          </p>
        </div>
      </section>

      {/* Product Catalog */}
      <section className="py-20 bg-theme-main">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
            <div>
              <h3 className="text-3xl font-black tracking-tight flex items-center gap-3">
                <Package className="text-brand" /> Our Catalog
              </h3>
              <p className="text-theme-muted mt-2 font-medium">Currently in stock and ready for you.</p>
            </div>
            
            <div className="relative w-full md:w-96">
              <input 
                type="text" 
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-theme-card border border-theme rounded-2xl py-4 px-6 text-theme-main focus:ring-2 focus:ring-brand outline-none shadow-soft transition-all font-bold"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-12">
            <button 
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${selectedCategory === 'all' ? 'bg-brand text-white shadow-lg' : 'bg-theme-card border border-theme text-theme-muted hover:border-brand hover:text-brand'}`}
            >
              All Items
            </button>
            {categories.map(cat => (
              <button 
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${selectedCategory === cat.id ? 'bg-brand text-white shadow-lg' : 'bg-theme-card border border-theme text-theme-muted hover:border-brand hover:text-brand'}`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {filteredProducts.length === 0 ? (
            <div className="text-center py-20 bg-theme-card rounded-3xl border border-theme border-dashed">
              <Package size={48} className="mx-auto text-theme-muted opacity-50 mb-4" />
              <h4 className="text-xl font-bold text-theme-main">No products found</h4>
              <p className="text-theme-muted mt-2">Try adjusting your search or category filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {filteredProducts.map(product => (
                <div key={product.id} className="group bg-theme-card rounded-3xl border border-theme shadow-sm hover:shadow-2xl hover:shadow-brand/20 transition-all duration-300 overflow-hidden flex flex-col hover:-translate-y-1">
                  <div className="aspect-[4/3] bg-theme-main p-8 flex items-center justify-center relative overflow-hidden group-hover:bg-brand/5 transition-colors">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500" />
                    ) : product.icon ? (
                      <span className="text-6xl group-hover:scale-110 transition-transform duration-500 select-none">{product.icon}</span>
                    ) : (
                      <Package size={64} className="text-theme-muted opacity-20 group-hover:scale-110 transition-transform duration-500" />
                    )}
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur text-brand text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-sm">
                      {product.category?.name || 'Item'}
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h4 className="text-lg font-bold text-theme-main leading-tight mb-2 group-hover:text-brand transition-colors">{product.name}</h4>
                    <p className="text-sm font-mono text-theme-muted mb-4">{product.sku}</p>
                    <div className="mt-auto flex items-end justify-between">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-theme-muted mb-1">Price</p>
                        <p className="text-2xl font-black text-theme-main">
                          {settings.currency_symbol || '$'}{parseFloat(product.price).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-theme-card border-t border-theme py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-theme-muted font-bold text-sm">
            &copy; {new Date().getFullYear()} {storeName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
